import type { ResError } from '@sysarb/resclient';
import { isResError, ResClient, ResCollection, ResModel } from '@sysarb/resclient';
import { reactive } from 'vue';
import { getApiHost } from '@/appSettings';
import type { useLoginManager } from '@/login/composables/useLoginManager';

// Set to true to log WebSocket events (open, close, error)
const SHOW_SOCKET_EVENTS = true;

type TraceCache = Map<number, string>;
export class ResgateManager {
    private static instance: ResgateManager | null = null;

    public static getInstance(): ResgateManager {
        ResgateManager.instance ??= new ResgateManager();
        return ResgateManager.instance;
    }

    public static destroy(): void {
        ResgateManager.instance = null;
    }

    public resClient?: ResClient;

    private readonly traces: TraceCache = new Map();

    public constructor() {
    }

    public static async connect(manager: ReturnType<typeof useLoginManager>): Promise<void> {
        return ResgateManager.getInstance()._connect(manager);
    }

    public static disconnect(): void {
        ResgateManager.getInstance()._disconnect();
    }

    private _disconnect(): void {
        this.resClient?.ws.close();
        this.resClient = undefined;
    }

    public static get client(): ResClient {
        return ResgateManager.getInstance().client;
    }

    public get client(): ResClient {
        if (this.resClient == null) {
            throw new Error('Resgate client is not connected');
        }

        return this.resClient;
    }

    private async _connect(manager: ReturnType<typeof useLoginManager>): Promise<void> {
        console.info('RESGATE', 'Connecting to Resgate...');

        const result = new Promise<void>((resolve, reject) => {
            try {
                const websocket = (): WebSocket => {
                    const sock = new WebSocket(`wss://${getApiHost()}`);

                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    if (SHOW_SOCKET_EVENTS) {
                        sock.onopen = (): void => {
                            console.info('RESGATE', 'WebSocket connection opened');
                        };
                        sock.onclose = (event): void => {
                            console.warn('RESGATE', 'WebSocket connection closed', event);
                        };
                        sock.onerror = (event): void => {
                            console.error('RESGATE', 'WebSocket error', event);
                        };
                    }

                    return sock;
                };

                this.resClient = new ResClient(websocket, {
                    debug: false,
                    onBeforeTrace: (id: number): { traceparent: string; tracestate: string } => {
                        const traceId = crypto.randomUUID();
                        this.traces.set(id, traceId);
                        return {
                            traceparent: traceId,
                            tracestate: 'sysarb=trace'
                        };
                    },
                    onAfterTrace: (success: boolean, id: number): void => {
                        // Try to get trace id from map
                        const traceId = this.traces.get(id);
                        if (traceId != null) {
                            this.traces.delete(id);
                        }
                    },
                    onConnect: async (): Promise<void> => {
                        console.info('RESGATE', 'Connected to Resgate');
                        await manager.loginResgate();
                        resolve();
                    }
                });

                this.resClient.registerModelType('>', (api: ResClient, rid: string) => reactive(new ResModel(api, rid)));
                this.resClient.registerCollectionType('>', (api: ResClient, rid: string) => reactive(new ResCollection(api, rid)));

                void this.resClient.connect();
            } catch (e: unknown) {
                console.error('RESGATE', 'Error connecting to Resgate', e);
                reject(new Error('Failed to connect to Resgate'));
            }
        });

        return result;
    }

    private validateSubject(subject?: string | null): boolean {
        if (subject == null) {
            console.error('Subject is required');
            return false;
        }

        if (subject === '') {
            console.error('Subject cannot be empty');
            return false;
        }

        if (subject.startsWith('#')) {
            console.error('Subject cannot start with a hash', subject);
            return false;
        }

        if (subject.includes(' ')) {
            console.error('Subject cannot contain spaces', subject);
            return false;
        }

        if (subject.includes('..')) {
            console.error('Subject cannot contain double dots', subject);
            return false;
        }

        if (subject.includes('.null.')) {
            console.error('Subject cannot contain NULL', subject);
            return false;
        }

        return true;
    }

    private validateConnection(rid: string): ResDataError | null {
        if (this.validateSubject(rid) === false) {
            return { success: false, data: null, error: createError('Invalid subject', rid) };
        }

        return null;
    }

    public static async model<T>(rid: string): Promise<Model<T> | ResErr> {
        const invalid = ResgateManager.getInstance().validateConnection(rid);

        if (invalid != null) {
            return invalid.error;
        }

        try {
            const response = await ResgateManager.client.get(rid);

            if (response instanceof ResCollection) {
                throw new Error('Expected model or error but got collection');
            }

            if (response instanceof ResModel) {
                return response as Model<T>;
            }

            return createError(response);
        } catch (e: unknown) {
            console.error('Error getting model', e);
            return createError(e);
        }
    }

    public async collection<T>(rid: string): Promise<Collection<T> | ResErr> {
        const invalid = this.validateConnection(rid);

        if (invalid != null) {
            return invalid.error;
        }

        try {
            const response = await this.client.get(rid);

            if (response instanceof ResModel) {
                throw new Error('Expected collection or error but got model');
            }

            if (response instanceof ResCollection) {
                return response as Collection<T>;
            }

            return createError(response);
        } catch (e: unknown) {
            return createError(e);
        }
    }

    public async call<T>(rid: string, method: string, data?: unknown): Promise<T | ResErr> {
        const invalid = this.validateConnection(rid);

        if (invalid != null) {
            return invalid.error;
        }

        if (method === '') {
            return createError('Method cannot be empty', rid);
        }

        try {
            const response = await ResgateManager.getInstance().client.call(rid, method, data).catch((e: unknown) => e);

            if (isResError(response) === true) {
                return createError(response);
            }

            return response as T;
        } catch (e: unknown) {
            return createError(e);
        }
    }
}

export type Model<T> = T & ResModel;

export type Collection<T> = Iterable<T> & Omit<ResCollection, 'list'> & { list: Model<T>[] };

type ResDataError = {
    success: false;
    error: ResErr;
    data: null;
};
export class ResErr {
    public rid: string;

    public method: string;

    public params: unknown;

    public message: string;

    public data: { details?: string; reasonId?: string };

    public constructor(err: ResError);
    public constructor(rid: string, method: string, params: unknown);
    public constructor(arg1: ResError | string, arg2?: string, arg3?: unknown) {
        if (typeof arg1 === 'string') {
            this.rid = arg1;
            this.method = arg2 ?? '';
            this.params = arg3 ?? null;
            this.message = 'Unknown error';
            this.data = { details: '', reasonId: '' };
        } else {
            this.rid = arg1.rid;
            this.method = arg1.method;
            this.params = arg1.params;
            this.message = arg1.message;
            this.data = arg1.data;
        }
    }
}

function createError(data: unknown, rid?: string): ResErr {
    if (isResError(data) === true) {
        return new ResErr(data as ResError);
    }

    if (typeof data === 'string') {
        return new ResErr(rid ?? '', '', data);
    }

    return new ResErr(rid ?? '', '', 'Unknown error');
}
